import { useAuth0 } from "@auth0/auth0-react";
import { useAtomValue } from "jotai";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui/button";
import { PlusIcon } from "~/components/ui/plus";
import {
  Sidebar,
  SidebarContent,
  SidebarTrigger,
} from "~/components/ui/sidebar";
import { SupplierBillingAdjustmentChargeTo } from "~/graphql/graphql-new.gen";
import { useSupplierBillingAdjustmentsQuery } from "~/graphql/graphql-new.gen";
import { isScopeAuthorized } from "~/libs/auth";
import { activeSaleIdState, activeSaleState } from "~/stores/sales";
import { formatPrice } from "~/utils/currency";

import { FinancialAdjustmentBadge } from "./financial-adjustment-badge";
import { FinancialAdjustmentModal } from "./financial-adjustment-modal";

type FinancialAdjustmentProps = {
  adjustment: {
    id: string;
    title: string;
    chargedTo: SupplierBillingAdjustmentChargeTo;
    description?: string | null;
    amountVatExcludedInCents?: number | null;
    amountVatIncludedInCents?: number | null;
    type:
      | "SupplierBillingAdjustmentDraft"
      | "SupplierBillingAdjustmentValidated";
  };
  isSubjectVat: boolean;
};

export const FinancialAdjustment: React.FC<FinancialAdjustmentProps> = ({
  adjustment,
  isSubjectVat,
}) => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const activeSale = useAtomValue(activeSaleState);

  return (
    <div className="flex flex-col gap-2 rounded border border-gray-100 p-6 md:flex-row md:items-center md:justify-between">
      <div className="flex flex-col gap-1 truncate">
        <div className="flex flex-col-reverse items-start gap-2 truncate md:flex-row md:items-center">
          <span className="truncate text-sm font-semibold text-gray-900">
            {adjustment.title}
          </span>
          <FinancialAdjustmentBadge type={adjustment.type} />
        </div>
        {adjustment.description ? (
          <p className="truncate text-sm text-[#767A7C]">
            {adjustment.description}
          </p>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-col items-start gap-6 md:flex-row md:items-center">
        {isSubjectVat ? (
          adjustment.amountVatIncludedInCents ? (
            <div className="space-x-1">
              <span className="font-semibold text-gray-900">
                {formatPrice(
                  ((adjustment.chargedTo ===
                  SupplierBillingAdjustmentChargeTo.Supplier
                    ? -1
                    : 1) *
                    adjustment.amountVatIncludedInCents) /
                    100,
                  i18n.language,
                  activeSale?.currency ?? "EUR",
                )}
              </span>
              <sup className="text-[#767A7C]">
                {t("invoice.financial_adjustments.vat_included")}
              </sup>
            </div>
          ) : null
        ) : adjustment.amountVatExcludedInCents ? (
          <div className="space-x-1">
            <span className="font-semibold text-gray-900">
              {formatPrice(
                ((adjustment.chargedTo ===
                SupplierBillingAdjustmentChargeTo.Supplier
                  ? -1
                  : 1) *
                  adjustment.amountVatExcludedInCents) /
                  100,
                i18n.language,
                activeSale?.currency ?? "EUR",
              )}
            </span>
            <sup className="text-[#767A7C]">
              {t("invoice.financial_adjustments.vat_excluded")}
            </sup>
          </div>
        ) : null}

        <Sidebar open={open} onOpenChange={setOpen}>
          <SidebarTrigger asChild>
            <Button type="button" variant="outline">
              {t("invoice.financial_adjustments.see")}
            </Button>
          </SidebarTrigger>
          <SidebarContent
            className="mt-18 sm:!w-[37.5rem] lg:mt-0"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <FinancialAdjustmentModal
              adjustmentId={adjustment.id}
              isEditModeDefault={false}
              setOpen={setOpen}
            />
          </SidebarContent>
        </Sidebar>
      </div>
    </div>
  );
};

export const FinancialAdjustments: React.FC<{
  isSubjectVat: boolean;
}> = ({ isSubjectVat }) => {
  const { user } = useAuth0();
  const { t } = useTranslation();
  const activeSaleId = useAtomValue(activeSaleIdState);
  const [open, setOpen] = useState(false);

  const { data: adjustments } = useSupplierBillingAdjustmentsQuery(
    {
      saleId: activeSaleId ?? "",
    },
    {
      select: (data) => data.supplierBillingAdjustments,
    },
  );

  const isInternalUser = !!user?.["https://appchoose.io/claims/isInternalUser"];

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-xl font-bold">
        {t("invoice.financial_adjustments.financial_adjustments")}
      </h3>
      <div className="flex flex-col gap-2">
        {adjustments?.map((adjustment) =>
          adjustment.__typename === "SupplierBillingAdjustmentDraft" ? (
            <FinancialAdjustment
              key={adjustment.supplierBillingAdjustmentId}
              adjustment={{
                id: adjustment.supplierBillingAdjustmentId,
                title: adjustment.draftTitle ?? "",
                chargedTo: adjustment.chargedTo,
                description: adjustment.draftDescription,
                amountVatExcludedInCents:
                  adjustment.draftAmountVatExcludedInCents,
                amountVatIncludedInCents:
                  adjustment.draftAmountVatIncludedInCents,
                type: adjustment.__typename,
              }}
              isSubjectVat={isSubjectVat}
            />
          ) : (
            <FinancialAdjustment
              key={adjustment.supplierBillingAdjustmentId}
              adjustment={{
                id: adjustment.supplierBillingAdjustmentId,
                title: adjustment.title,
                chargedTo: adjustment.chargedTo,
                description: adjustment.description,
                amountVatExcludedInCents: adjustment.amountVatExcludedInCents,
                amountVatIncludedInCents: adjustment.amountVatIncludedInCents,
                type: adjustment.__typename,
              }}
              isSubjectVat={isSubjectVat}
            />
          ),
        )}
        {isInternalUser ? (
          <div>
            <Sidebar open={open} onOpenChange={setOpen}>
              <SidebarTrigger
                className="flex items-center gap-1 text-green-900 focus-visible:ring-2 focus-visible:ring-gray-900/30 disabled:opacity-50"
                disabled={
                  !isScopeAuthorized(
                    user,
                    "scope.brand.supplier-billing.adjustment.create",
                  )
                }
              >
                <PlusIcon className="size-4" />
                <span className="text-xs font-semibold">
                  {t("invoice.financial_adjustments.add_financial_adjustment")}
                </span>
              </SidebarTrigger>
              <SidebarContent
                className="mt-18 sm:!w-[37.5rem] lg:mt-0"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <FinancialAdjustmentModal
                  isEditModeDefault={true}
                  setOpen={setOpen}
                />
              </SidebarContent>
            </Sidebar>
          </div>
        ) : null}
      </div>
    </div>
  );
};
